import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { dashboardService } from '../services/api';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, Area, AreaChart } from 'recharts';
import { FileText, Users, TrendingUp, Clock, Award, Filter, Download } from 'lucide-react';
import { AutocompleteInput } from '../components/AutocompleteInput';

const COLORS = ['#3B82F6', '#6366F1', '#8B5CF6', '#10B981', '#F59E0B'];
const EVAL_COLORS = {
  'En dessous de la moyenne': '#EF4444',
  'Dans la moyenne': '#F59E0B',
  'Au-dessus de la moyenne': '#10B981',
  'Très bon niveau': '#3B82F6'
};
const SCORE_COLORS = ['#EF4444', '#F97316', '#F59E0B', '#10B981', '#3B82F6'];

const TOOLTIP_STYLE = {
  background: '#111D35',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  fontSize: '13px',
  color: '#E2E8F0',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
};

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    fonction: '',
    niveau: '',
    type_questionnaire: '',
    client: '',
    interlocuteur_client: '',
    ref_ao: '',
    tag: '',
    evaluateur: ''
  });

  useEffect(() => {
    fetchStats();
  }, [filters]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.fonction) params.fonction = filters.fonction;
      if (filters.niveau) params.niveau = filters.niveau;
      if (filters.type_questionnaire) params.type_questionnaire = filters.type_questionnaire;
      if (filters.client) params.client = filters.client;
      if (filters.interlocuteur_client) params.interlocuteur_client = filters.interlocuteur_client;
      if (filters.ref_ao) params.ref_ao = filters.ref_ao;
      if (filters.tag) params.tag = filters.tag;
      if (filters.evaluateur) params.evaluateur = filters.evaluateur;
      const response = await dashboardService.getStats(params);
      setStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      await dashboardService.exportPdf(filters);
      toast.success('Tableau de bord exporté en PDF');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  if (loading && !stats) {
    return (
      <Layout title="Tableau de bord" breadcrumbs={['Accueil', 'Tableau de bord']}>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-400">Chargement...</p>
        </div>
      </Layout>
    );
  }

  const chartData = Object.entries(stats?.tests_by_position || {}).map(([name, value]) => ({
    name,
    value
  }));

  const levelData = Object.entries(stats?.tests_by_level || {}).map(([name, value]) => ({
    name,
    value
  }));

  const scoreDistData = (stats?.score_distribution || []).map((item, i) => ({
    ...item,
    fill: SCORE_COLORS[i]
  }));

  const evalData = Object.entries(stats?.evaluation_distribution || {}).map(([name, value]) => ({
    name,
    value
  }));

  // Best score among recent tests
  const bestScore = stats?.recent_tests?.reduce((max, t) => Math.max(max, t.average_score || 0), 0) || 0;

  return (
    <Layout title="Tableau de bord" breadcrumbs={['Accueil', 'Tableau de bord']}>
      {/* Export bar */}
      <div className="flex justify-end mb-4">
        <button
          onClick={handleExportPdf}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-500 text-white text-sm font-medium hover:from-blue-500 hover:to-indigo-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-900/30"
          data-testid="export-dashboard-pdf-button"
        >
          <Download size={16} strokeWidth={2} />
          {exporting ? 'Export en cours...' : 'Exporter en PDF'}
        </button>
      </div>

      {/* Filters */}
      <div className="eva-card mb-6" data-testid="dashboard-filters">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Filter size={18} className="text-slate-400" />
            <h3 className="font-semibold text-white">Filtres</h3>
          </div>
          {hasActiveFilters && (
            <button
              onClick={() => setFilters({ fonction: '', niveau: '', type_questionnaire: '', client: '', interlocuteur_client: '', ref_ao: '', tag: '', evaluateur: '' })}
              className="text-xs text-slate-400 hover:text-white transition-colors"
              data-testid="clear-filters-button"
            >
              Réinitialiser
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="eva-label">Poste</label>
            <AutocompleteInput
              value={filters.fonction}
              onChange={(val) => handleFilterChange({ ...filters, fonction: val })}
              endpoint="/autocomplete/fonctions"
              className="eva-input"
              placeholder="Tous"
              dataTestId="dashboard-filter-fonction"
            />
          </div>
          <div>
            <label className="eva-label">Niveau</label>
            <select
              value={filters.niveau}
              onChange={(e) => handleFilterChange({ ...filters, niveau: e.target.value })}
              className="eva-input"
              data-testid="dashboard-filter-niveau"
            >
              <option value="">Tous</option>
              <option value="Junior">Junior</option>
              <option value="Confirmé">Confirmé</option>
              <option value="Senior">Senior</option>
              <option value="Expert">Expert</option>
            </select>
          </div>
          <div>
            <label className="eva-label">Type</label>
            <select
              value={filters.type_questionnaire}
              onChange={(e) => handleFilterChange({ ...filters, type_questionnaire: e.target.value })}
              className="eva-input"
              data-testid="dashboard-filter-type"
            >
              <option value="">Tous</option>
              <option value="Générique">Générique</option>
              <option value="Appel d'offres">Appel d'offres</option>
            </select>
          </div>
          <div>
            <label className="eva-label">Réf. AO</label>
            <AutocompleteInput
              value={filters.ref_ao}
              onChange={(val) => handleFilterChange({ ...filters, ref_ao: val })}
              endpoint="/autocomplete/ref_ao"
              className="eva-input"
              placeholder="Tous"
              dataTestId="dashboard-filter-ref-ao"
            />
          </div>
          <div>
            <label className="eva-label">Entreprise</label>
            <AutocompleteInput
              value={filters.client}
              onChange={(val) => handleFilterChange({ ...filters, client: val })}
              endpoint="/autocomplete/entreprises"
              className="eva-input"
              placeholder="Tous"
              dataTestId="dashboard-filter-entreprise"
            />
          </div>
          <div>
            <label className="eva-label">Opérationnel</label>
            <AutocompleteInput
              value={filters.interlocuteur_client}
              onChange={(val) => handleFilterChange({ ...filters, interlocuteur_client: val })}
              endpoint="/autocomplete/interlocuteurs"
              className="eva-input"
              placeholder="Tous"
              dataTestId="dashboard-filter-interlocuteur"
            />
          </div>
          <div>
            <label className="eva-label">Tag technique</label>
            <AutocompleteInput
              value={filters.tag}
              onChange={(val) => handleFilterChange({ ...filters, tag: val })}
              endpoint="/autocomplete/tags"
              className="eva-input"
              placeholder="Tous"
              dataTestId="dashboard-filter-tag"
            />
          </div>
          <div>
            <label className="eva-label">Évaluateur</label>
            <AutocompleteInput
              value={filters.evaluateur}
              onChange={(val) => handleFilterChange({ ...filters, evaluateur: val })}
              endpoint="/autocomplete/evaluateurs"
              className="eva-input"
              placeholder="Tous"
              dataTestId="dashboard-filter-evaluateur"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-testid="dashboard-stats">
        <div className="eva-card" data-testid="total-tests-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">
                Tests réalisés
              </p>
              <p className="text-3xl font-bold text-white">{stats?.total_tests || 0}</p>
            </div>
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <FileText size={24} className="text-white" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        <div className="eva-card" data-testid="average-score-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">
                Score moyen
              </p>
              <p className="text-3xl font-bold text-white">{stats?.average_score || 0}/10</p>
            </div>
            <div className="w-12 h-12 bg-emerald-500/15 rounded-lg flex items-center justify-center">
              <TrendingUp size={24} className="text-[#10B981]" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        <div className="eva-card" data-testid="candidates-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">
                Candidats évalués
              </p>
              <p className="text-3xl font-bold text-white">{stats?.total_tests || 0}</p>
            </div>
            <div className="w-12 h-12 bg-slate-500/15 rounded-lg flex items-center justify-center">
              <Users size={24} className="text-[#3A506B]" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        <div className="eva-card" data-testid="best-score-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">
                Meilleur score
              </p>
              <p className="text-3xl font-bold text-white">{bestScore.toFixed(1)}/10</p>
            </div>
            <div className="w-12 h-12 bg-amber-500/15 rounded-lg flex items-center justify-center">
              <Award size={24} className="text-[#F59E0B]" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Bar Chart - Tests by Position */}
        {chartData.length > 0 && (
          <div className="eva-card" data-testid="tests-by-position-chart">
            <h3 className="text-lg font-semibold text-white mb-4">Tests par poste</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} allowDecimals={false} />
                <Tooltip 
                  contentStyle={TOOLTIP_STYLE}
                />
                <Bar dataKey="value" name="Tests" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Pie Chart - Tests by Seniority Level */}
        {levelData.length > 0 && (
          <div className="eva-card" data-testid="tests-by-level-chart">
            <h3 className="text-lg font-semibold text-white mb-4">Répartition par niveau</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={levelData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, x, y }) => (
                    <text x={x} y={y} fill="#94A3B8" fontSize={12} textAnchor="middle" dominantBaseline="central">
                      {name} ({(percent * 100).toFixed(0)}%)
                    </text>
                  )}
                  outerRadius={100}
                  dataKey="value"
                >
                  {levelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Score Distribution Histogram */}
        {scoreDistData.some(d => d.count > 0) && (
          <div className="eva-card" data-testid="score-distribution-chart">
            <h3 className="text-lg font-semibold text-white mb-4">Distribution des scores</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={scoreDistData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value) => [`${value} candidat(s)`, 'Nombre']}
                />
                <Bar dataKey="count" name="Candidats" radius={[4, 4, 0, 0]}>
                  {scoreDistData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={SCORE_COLORS[index]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Evaluation Distribution Pie Chart */}
        {evalData.length > 0 && (
          <div className="eva-card" data-testid="evaluation-distribution-chart">
            <h3 className="text-lg font-semibold text-white mb-4">Répartition des évaluations</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={evalData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percent, x, y }) => (
                    <text x={x} y={y} fill="#94A3B8" fontSize={12} textAnchor="middle" dominantBaseline="central">
                      {(percent * 100).toFixed(0)}%
                    </text>
                  )}
                  outerRadius={100}
                  innerRadius={50}
                  dataKey="value"
                  paddingAngle={2}
                >
                  {evalData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={EVAL_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend 
                  verticalAlign="bottom"
                  formatter={(value) => <span className="text-sm text-slate-300">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Temporal Stats - Tests over time */}
      {stats?.tests_over_time && stats.tests_over_time.some(d => d.count > 0) && (
        <div className="eva-card mb-8" data-testid="tests-over-time-chart">
          <h3 className="text-lg font-semibold text-white mb-4">Activité sur 12 mois</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={stats.tests_over_time}>
              <defs>
                <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
              <YAxis yAxisId="left" tick={{ fill: '#64748b', fontSize: 12 }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: '#10B981', fontSize: 12 }} domain={[0, 5]} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value, name) => {
                  if (name === 'Tests') return [`${value}`, name];
                  return [`${value}/10`, name];
                }}
              />
              <Legend verticalAlign="top" height={36} />
              <Area yAxisId="left" type="monotone" dataKey="count" name="Tests" stroke="#3B82F6" strokeWidth={2} fill="url(#colorCount)" />
              <Area yAxisId="right" type="monotone" dataKey="avg_score" name="Score moyen" stroke="#10B981" strokeWidth={2} fill="url(#colorScore)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent Tests */}
      <div className="eva-card" data-testid="recent-tests">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Derniers tests réalisés</h3>
          <Clock size={20} className="text-slate-400" strokeWidth={1.5} />
        </div>

        {stats?.recent_tests && stats.recent_tests.length > 0 ? (
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
            {stats.recent_tests.map((test) => (
              <div 
                key={test.id} 
                className="flex items-center justify-between p-3 border border-white/10 rounded-md hover:bg-white/5 transition-colors cursor-pointer"
                onClick={() => navigate(`/rapport/${test.id}`)}
                data-testid={`recent-test-${test.id}`}
              >
                <div>
                  <p className="text-sm font-medium text-white">{test.test_name}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {new Date(test.date).toLocaleDateString('fr-FR')}
                    {test.evaluator_name && <span> par <span className="text-slate-300">{test.evaluator_name}</span></span>}
                  </p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1">
                    {[...Array(10)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.round(test.average_score || 0) ? 'text-[#F59E0B]' : 'text-[#E9ECEF]'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-slate-400">{test.average_score?.toFixed(1)}/10</p>
                    {test.evaluation && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        test.evaluation === 'Très bon niveau' ? 'bg-blue-600/20 text-blue-400 border border-blue-500/20' :
                        test.evaluation === 'Au-dessus de la moyenne' ? 'bg-[#10B981] bg-opacity-15 text-[#10B981]' :
                        test.evaluation === 'Dans la moyenne' ? 'bg-[#F59E0B] bg-opacity-15 text-[#F59E0B]' :
                        'bg-[#EF4444] bg-opacity-15 text-[#EF4444]'
                      }`}>{test.evaluation}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <p className="text-sm">Aucun test réalisé pour le moment</p>
          </div>
        )}
      </div>
    </Layout>
  );
};
